import { useEffect, useState } from "react";
import { SearchIcon, SlidersHorizontalIcon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  canQuickAdd,
  countActiveFilters,
  getListPrice,
  getProductHandle,
  hasPriceRange,
  isSoldOut,
  StorefrontSorts,
  type StorefrontSearch,
  type StorefrontSort,
} from "@/lib/storefront/catalog-filters";
import type { Product } from "@/lib/products";
import { useCatalogView } from "@/hooks/storefront/use-catalog-view";
import { useDebouncedValue } from "@/hooks/storefront/use-debounced-value";
import {
  AddToCartResults,
  useStorefrontAddToCart,
} from "@/hooks/storefront/use-storefront-add-to-cart";
import { useLocaleNavigation } from "@/hooks/use-locale-navigation";
import { CatalogFilters } from "./catalog-filters";
import {
  CatalogProductCard,
  QuickAddStates,
  type QuickAddState,
} from "./catalog-product-card";

interface StorefrontCatalogProps {
  search: StorefrontSearch;
  onSearchChange: (patch: Partial<StorefrontSearch>) => void;
  heroImage?: string;
  heroDescription?: string;
}

const SORT_LABEL_KEYS: Record<StorefrontSort, string> = {
  [StorefrontSorts.featured]: "Storefront.Sort.featured",
  [StorefrontSorts.newest]: "Storefront.Sort.newest",
  [StorefrontSorts.priceAsc]: "Storefront.Sort.price_asc",
  [StorefrontSorts.priceDesc]: "Storefront.Sort.price_desc",
  [StorefrontSorts.nameAsc]: "Storefront.Sort.name_asc",
};

function getQuickAddState(product: Product): QuickAddState {
  if (isSoldOut(product)) return QuickAddStates.soldOut;
  return canQuickAdd(product)
    ? QuickAddStates.add
    : QuickAddStates.chooseOptions;
}

/** The webshop's product catalogue: filters, search, sort and the grid. */
export function StorefrontCatalog({
  search,
  onSearchChange,
  heroImage,
  heroDescription,
}: StorefrontCatalogProps) {
  const { t } = useTranslation("translation");
  const view = useCatalogView(search);
  const { quickAdd } = useStorefrontAddToCart();
  const { navigateWithLocale } = useLocaleNavigation();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const activeFilterCount = countActiveFilters(search);

  const handleQuickAdd = async (product: Product) => {
    const result = await quickAdd(product);
    if (result === AddToCartResults.needsOptions) {
      navigateWithLocale(`/products/${getProductHandle(product)}`);
    }
  };

  const filters = (
    <CatalogFilters
      search={search}
      onSearchChange={onSearchChange}
      categoryFacets={view.categoryFacets}
      tagFacets={view.tagFacets}
      priceBounds={view.priceBounds}
      totalProductCount={view.categoryFacets.reduce(
        (sum, category) => sum + category.count,
        0
      )}
    />
  );

  return (
    <div className="flex flex-1 flex-col">
      {(heroImage || heroDescription) && !search.category && !search.q && (
        <CatalogHero image={heroImage} description={heroDescription} />
      )}

      <div className="mx-auto flex w-full max-w-7xl flex-1 gap-10 px-4 py-6 lg:py-10">
        <aside className="hidden w-60 shrink-0 lg:block">{filters}</aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-col gap-4">
            <div className="flex items-baseline justify-between gap-4">
              <h1 className="text-2xl font-bold lg:text-3xl">
                {view.activeCategory?.name ?? t("Storefront.Catalog.title")}
              </h1>
              <p className="shrink-0 text-sm text-gray-500">
                {t("Storefront.Catalog.result_count", {
                  count: view.totalCount,
                })}
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <CatalogSearchInput
                value={search.q ?? ""}
                onChange={(q) => onSearchChange({ q: q || undefined })}
              />
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setIsFilterDrawerOpen(true)}
                  className="flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-2 rounded-full border px-4 font-medium hover:bg-gray-50 lg:hidden"
                >
                  <SlidersHorizontalIcon className="size-4" />
                  {t("Storefront.Filters.title")}
                  {activeFilterCount > 0 && (
                    <span className="flex size-5 items-center justify-center rounded-full bg-black text-xs text-white">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                <Select
                  value={search.sort ?? StorefrontSorts.featured}
                  onValueChange={(value) =>
                    onSearchChange({
                      sort:
                        value === StorefrontSorts.featured
                          ? undefined
                          : (value as StorefrontSort),
                    })
                  }
                >
                  <SelectTrigger
                    className="min-h-11 flex-1 rounded-full px-4 sm:w-52 sm:flex-none"
                    aria-label={t("Storefront.Sort.label")}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(StorefrontSorts).map((sort) => (
                      <SelectItem key={sort} value={sort} className="min-h-11">
                        {t(SORT_LABEL_KEYS[sort] as never)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={() =>
                  onSearchChange({
                    category: undefined,
                    tags: undefined,
                    min: undefined,
                    max: undefined,
                    inStock: undefined,
                  })
                }
                className="flex min-h-11 w-fit cursor-pointer items-center gap-1 rounded-full px-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                <XIcon className="size-4" />
                {t("Storefront.Filters.clear")}
              </button>
            )}
          </div>

          {view.totalCount === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 py-20 text-center">
              <p className="text-lg font-medium">
                {t("Storefront.Catalog.empty_title")}
              </p>
              <p className="text-gray-500">
                {t("Storefront.Catalog.empty_description")}
              </p>
            </div>
          ) : (
            <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-6">
              {view.products.map((product) => (
                <li key={product.id} className="flex">
                  <CatalogProductCard
                    product={product}
                    href={`/products/${getProductHandle(product)}`}
                    price={getListPrice(product)}
                    isFromPrice={hasPriceRange(product)}
                    quickAddState={getQuickAddState(product)}
                    onQuickAdd={() => void handleQuickAdd(product)}
                  />
                </li>
              ))}
            </ul>
          )}

          {view.hasMore && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={view.showMore}
                className="min-h-11 cursor-pointer rounded-full border px-8 font-medium hover:bg-gray-50"
              >
                {t("Storefront.Catalog.show_more")}
              </button>
            </div>
          )}
        </div>
      </div>

      <Drawer
        direction="bottom"
        open={isFilterDrawerOpen}
        onOpenChange={setIsFilterDrawerOpen}
      >
        <DrawerContent className="data-[vaul-drawer-direction=bottom]:max-h-[90dvh]">
          <DrawerHeader className="flex flex-row items-center justify-between border-b">
            <DrawerTitle>{t("Storefront.Filters.title")}</DrawerTitle>
            <DrawerDescription className="sr-only">
              {t("Storefront.Filters.description")}
            </DrawerDescription>
            <DrawerClose
              className="flex size-11 cursor-pointer items-center justify-center rounded-full hover:bg-gray-100"
              aria-label={t("Storefront.Filters.close")}
            >
              <XIcon className="size-5" />
            </DrawerClose>
          </DrawerHeader>
          <div className="overflow-y-auto p-4">{filters}</div>
          <DrawerFooter className="border-t">
            <DrawerClose className="min-h-12 w-full cursor-pointer rounded-full bg-black px-6 text-lg font-bold text-white hover:bg-black/85">
              {t("Storefront.Filters.show_results", {
                count: view.totalCount,
              })}
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function CatalogHero({
  image,
  description,
}: {
  image?: string;
  description?: string;
}) {
  return (
    <section
      className="relative flex min-h-48 items-center bg-gray-100 bg-cover bg-center lg:min-h-72"
      style={image ? { backgroundImage: `url(${image})` } : undefined}
    >
      {image && <div className="absolute inset-0 bg-black/30" />}
      {description && (
        <div className="relative mx-auto w-full max-w-7xl px-4 py-8">
          {/* The same white card the ordering site shows the text in, which
              is what merchants write it for. */}
          <div
            className="max-w-xl rounded-xl border bg-white p-5 text-black shadow"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      )}
    </section>
  );
}

function CatalogSearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation("translation");
  const [text, setText] = useState(value);
  const [syncedValue, setSyncedValue] = useState(value);
  const debounced = useDebouncedValue(text, 300);

  // The URL's query changed from elsewhere (back button, "clear"): show it.
  // Adjusted while rendering rather than in an effect, so the field never
  // flashes the old text.
  if (value !== syncedValue) {
    setSyncedValue(value);
    if (value !== text.trim()) setText(value);
  }

  // Typing settles into the URL once the customer pauses.
  useEffect(() => {
    if (debounced.trim() !== value) onChange(debounced.trim());
  }, [debounced]);

  return (
    <label className="flex min-h-11 flex-1 items-center gap-2 rounded-full border bg-white px-4 focus-within:ring-2 focus-within:ring-black/20">
      <SearchIcon className="size-4 shrink-0 text-gray-500" />
      <span className="sr-only">{t("Storefront.Catalog.search")}</span>
      <input
        type="search"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={t("Storefront.Catalog.search_placeholder")}
        className="w-full min-w-0 bg-transparent py-2 text-base outline-none"
      />
    </label>
  );
}
